// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import * as Sentry from '@sentry/react';
import { Opaque } from '@/utilities/opaque';
import { isEmpty } from 'lodash-es';
import { DateTime, IANAZone } from 'luxon';

// NormalizedDateTime is a DateTime that is normalized to 12:00 UTC.
type NormalizedDateTime = Opaque<DateTime, 'NormalizedDateTime'>;

/**
 * Normalizes a DateTime to 12:00 UTC.
 *
 * @param dateTime - A DateTime object.
 */
export function normalizeDateTime(dateTime: DateTime): NormalizedDateTime {
  const normalizedDateTime = dateTime
    .set({ hour: 12, minute: 0, second: 0, millisecond: 0 })
    .setZone('UTC', { keepLocalTime: true });

  return normalizedDateTime as NormalizedDateTime;
}

/**
 * Determines if a DateTime is normalized to 12:00 UTC.
 *
 * @param dateTime - A DateTime object.
 */
function isNormalizedDateTime(
  dateTime: DateTime
): dateTime is NormalizedDateTime {
  const isValidOffset = dateTime.offset === 0;
  const isValidTime =
    dateTime.hour === 12 &&
    dateTime.minute === 0 &&
    dateTime.second === 0 &&
    dateTime.millisecond === 0;

  return isValidOffset && isValidTime;
}

/**
 * Determines if an ISO string represents a date-time that is normalized to
 * 12:00 UTC.
 *
 * @param iso - An ISO string.
 */
export function isNormalizedISO(iso: string) {
  const dateTime = DateTime.fromISO(iso, { setZone: true });

  return isNormalizedDateTime(dateTime);
}

/**
 * Returns a DateTime object representing the start of the current day.
 */
export function getToday() {
  return DateTime.now().startOf('day');
}

/**
 * Formats a DateTime for display as a short calendar date, such as
 * "Apr 22, 2026" — short month, numeric day, numeric year, rendered in the
 * browser's locale.
 *
 * The input must be a valid DateTime; callers holding a nullable or possibly
 * invalid value should guard before calling. UTC-axis labels that format in
 * coordinate space rather than local display time are intentionally separate
 * from this helper.
 *
 * @param dateTime - A valid DateTime object.
 */
export function formatShortDate(dateTime: DateTime): string {
  return dateTime.toLocaleString({
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

const DATE_FORMATS = [
  'yyyy-MM-dd', // YYYY-MM-DD
  'MMM d, yyyy', // MMM D, YYYY
  'MMMM d, yyyy', // MMMM D, YYYY
  'MMM dd, yyyy', // MMM DD, YYYY
  'MMMM dd, yyyy', // MMMM DD, YYYY
] as const;

const TIME_FORMATS = [
  'HH:mm', // 24-hour format (HH:MM)
  'HH:mm:ss', // 24-hour format with seconds (HH:MM:SS)
  'hh:mm a', // 12-hour format (HH:MM AM/PM)
  'hh:mm:ss a', // 12-hour format with seconds (HH:MM:SS AM/PM)
] as const;

const DATETIME_FORMATS = [
  'yyyy-MM-dd HH:mm', // YYYY-MM-DD HH:MM
  'yyyy-MM-dd HH:mm:ss', // YYYY-MM-DD HH:MM:SS
  'yyyy-MM-dd hh:mm a', // YYYY-MM-DD HH:MM AM/PM
  'yyyy-MM-dd hh:mm:ss a', // YYYY-MM-DD HH:MM:SS AM/PM
  'MMM d, yyyy HH:mm', // MMM D, YYYY HH:MM
  'MMM d, yyyy HH:mm:ss', // MMM D, YYYY HH:MM:SS
  'MMM d, yyyy hh:mm a', // MMM D, YYYY HH:MM AM/PM
  'MMM d, yyyy hh:mm:ss a', // MMM D, YYYY HH:MM:SS AM/PM
  'MMMM d, yyyy HH:mm', // MMMM D, YYYY HH:MM
  'MMMM d, yyyy HH:mm:ss', // MMMM D, YYYY HH:MM:SS
  'MMMM d, yyyy hh:mm a', // MMMM D, YYYY HH:MM AM/PM
  'MMMM d, yyyy hh:mm:ss a', // MMMM D, YYYY HH:MM:SS AM/PM
  'MMM dd, yyyy HH:mm', // MMM DD, YYYY HH:MM
  'MMM dd, yyyy HH:mm:ss', // MMM DD, YYYY HH:MM:SS
  'MMM dd, yyyy hh:mm a', // MMM DD, YYYY HH:MM AM/PM
  'MMM dd, yyyy hh:mm:ss a', // MMM DD, YYYY HH:MM:SS AM/PM
  'MMMM dd, yyyy HH:mm', // MMMM DD, YYYY HH:MM
  'MMMM dd, yyyy HH:mm:ss', // MMMM DD, YYYY HH:MM:SS
  'MMMM dd, yyyy hh:mm a', // MMMM DD, YYYY HH:MM AM/PM
  'MMMM dd, yyyy hh:mm:ss a', // MMMM DD, YYYY HH:MM:SS AM/PM
] as const;

/**
 * Parses and formats a date string into a standardized format.
 * Tries multiple common date formats before giving up.
 *
 * @param value - The date string to parse
 * @returns An object with the parsed value and validation status
 */
export function parseAndFormatDate(value: string): {
  value: string | null;
  isValid: boolean;
} {
  // Empty string or null means user wants to clear the field
  if (isEmpty(value)) {
    return { value: null, isValid: true };
  }

  for (const format of DATE_FORMATS) {
    try {
      const parsedDate = DateTime.fromFormat(value, format);
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (parsedDate.isValid) {
        return { value: parsedDate.toFormat('yyyy-MM-dd'), isValid: true };
      }
    } catch (_e) {
      // Continue to next format if parsing fails
      // This case is needed because user can provide valid date inputs
      // that are not in the expected format (e.g., "2024" is a valid date for
      // a year field but will fail to parse to 'yyyy-MM-dd')
    }
  }

  // If we've exhausted all formats and none worked, log the error
  Sentry.captureException(new Error(`Failed to parse date: ${value}`));
  return { value: null, isValid: false };
}

/**
 * Parses and formats a time string into a standardized format.
 * Tries multiple common time formats before giving up.
 *
 * @param value - The time string to parse
 * @returns An object with the parsed value and validation status
 */
export function parseAndFormatTime(value: string): {
  value: string | null;
  isValid: boolean;
} {
  // Empty string or null means user wants to clear the field
  if (isEmpty(value)) {
    return { value: null, isValid: true };
  }

  for (const format of TIME_FORMATS) {
    try {
      const parsedTime = DateTime.fromFormat(value, format);
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (parsedTime.isValid) {
        return { value: parsedTime.toFormat('HH:mm:ss'), isValid: true };
      }
    } catch (_e) {
      // Continue to next format if parsing fails
      // This case is needed because user can provide valid time inputs
      // that are not in the expected format (e.g., "2024" is a valid date for
      // a year field but will fail to parse for 'HH:mm:ss')
    }
  }
  Sentry.captureException(new Error(`Failed to parse time: ${value}`));
  return { value: null, isValid: false };
}

/**
 * Parses and formats a datetime string into a standardized format.
 * Tries multiple common datetime formats before giving up.
 *
 * @param value - The datetime string to parse
 * @returns An object with the parsed value and validation status
 */

export function parseAndFormatDateTime(value: string): {
  value: string | null;
  isValid: boolean;
} {
  // Empty string or null means user wants to clear the field
  if (isEmpty(value)) {
    return { value: null, isValid: true };
  }

  for (const format of DATETIME_FORMATS) {
    try {
      const parsedDateTime = DateTime.fromFormat(value, format);
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (parsedDateTime.isValid) {
        return {
          value: parsedDateTime.toFormat('yyyy-MM-dd HH:mm:ss'),
          isValid: true,
        };
      }
    } catch (_e) {
      // Continue to next format if parsing fails
      // This case is needed because user can provide valid date/time inputs
      // that are not in the expected format (e.g., "2024" is a valid date for
      // a year field but will fail to parse for 'yyyy-MM-dd HH:mm:ss')
    }
  }
  Sentry.captureException(new Error(`Failed to parse date-time: ${value}`));
  return { value: null, isValid: false };
}

type SerializedDateForAPI<T extends DateTime | null | undefined> =
  T extends DateTime ? string : T;

/**
 * Serializes date values for backend DATE request fields.
 *
 * This preserves the DateTime's calendar date and returns YYYY-MM-DD.
 * Null is preserved for nullable fields, and undefined is preserved so
 * optional PATCH fields remain omitted.
 * Timestamp request fields are a different backend type and should continue
 * to use DateTime.toISO()
 *
 * The generic return type keeps caller-specific DateTime/null/undefined
 * precision. TypeScript cannot prove the conditional mapping from the
 * runtime branch, so the implementation uses a final assertion.
 */
export function serializeDateForAPI<T extends DateTime | null | undefined>(
  dateTime: T
): SerializedDateForAPI<T> {
  // Widen the generic before the nullish check so lint and TypeScript can
  // narrow the concrete runtime union.
  const dateTimeValue: DateTime | null | undefined = dateTime;
  const serializedDate =
    dateTimeValue === null || dateTimeValue === undefined
      ? dateTimeValue
      : dateTimeValue.toFormat('yyyy-MM-dd');

  return serializedDate as SerializedDateForAPI<T>;
}

/** How a screen spells out a calendar date when it has room for the month. */
export const MEDIUM_DATE_FORMAT: Intl.DateTimeFormatOptions = {
  dateStyle: 'medium',
};

/** How a screen spells out the moment something happened. */
const MEDIUM_TIMESTAMP_FORMAT: Intl.DateTimeFormatOptions = {
  dateStyle: 'medium',
  timeStyle: 'short',
};

/**
 * Spells out a moment in the zone it was recorded in, naming that zone.
 *
 * A record read in the reader's own zone would have two people quoting
 * different times for one event, and without the zone beside it the reader has
 * no way to tell the clock is not theirs.
 */
/** Names the day a moment was recorded on, in the zone that recorded it. */
export function toRecordedDate(at: DateTime, timezone: string): string {
  return toRecordedTime(at, timezone).toLocaleString(MEDIUM_DATE_FORMAT);
}

export function toRecordedTimestamp(at: DateTime, timezone: string): string {
  const recorded = toRecordedTime(at, timezone);
  return `${recorded.toLocaleString(MEDIUM_TIMESTAMP_FORMAT)} ${recorded.toFormat('ZZZZ')}`;
}

/**
 * Reads an instant in the zone it was recorded in, so every reader sees the
 * moment the record named rather than the same instant in their own zone.
 */
export function toRecordedTime(at: DateTime, timezone: string) {
  // A record kept its zone years ago and the tz database moves on. A name
  // luxon no longer knows reads in UTC rather than as "Invalid DateTime", and
  // rather than in the reader's own zone: whatever wrote the prose beside this
  // fell back to UTC too, and the two naming different days is worse than
  // either naming an unfamiliar one.
  const zone = IANAZone.isValidZone(timezone) ? timezone : 'utc';
  return at.setZone(zone);
}

// Anything under a minute reads as "seconds ago" with no count. The number is
// stale the instant it renders, and nothing re-renders on a timer, so a view
// left open sits there claiming 30 seconds for as long as somebody reads it.
const SECONDS_PER_MINUTE = 60;

/** Renders when something happened, relative to now. */
export function toRelativeTime(at: DateTime) {
  const now = DateTime.now();
  if (now.diff(at, 'seconds').seconds < SECONDS_PER_MINUTE) {
    return 'seconds ago';
  }
  return at.toRelative({ base: now });
}
